import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { 
    Box,
    Typography,
    Chip,
    IconButton,
    TextField,
    Button,
    Paper,
    Snackbar,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
    TableSortLabel,
} from '@mui/material';
import { 
    Edit as EditIcon, 
    Archive as ArchiveIcon, 
    Unarchive as UnarchiveIcon,
    Add as AddIcon,
    Clear as ClearIcon,
    Search as SearchIcon,
    EventNoteOutlined as EventNoteOutlinedIcon,
    ArrowForward as BackIcon,
} from '@mui/icons-material';
import SearchableAutocomplete from '../../../SearchableAutocomplete';
import TablePagination from '../UserList/TablePagination';
import useFetch from '../../../../hooks/useFetch';
import api from '../../../../config/axiosConfig';
import { toPersianDigits } from '../../../../utils/convertNumbers';
import { formatJalaliDate } from '../../../../utils/formatJalaliDate';
import RTL from '../../../../utils/RTL';
import StandardTable from '../../../common/table/StandardTable';
import StudentRegistrationForm from '../StudentRegistration/StudentRegistrationForm';
import { useAuth } from '../../../../context/AuthContext';

const DAY_CHOICES = [
    { value: 0, label: 'شنبه' },
    { value: 1, label: 'یکشنبه' },
    { value: 2, label: 'دوشنبه' },
    { value: 3, label: 'سه‌شنبه' },
    { value: 4, label: 'چهارشنبه' },
    { value: 5, label: 'پنج‌شنبه' },
    { value: 6, label: 'جمعه' },
];

function formatTime(timeString) {
    if (!timeString) return '-';
    return toPersianDigits(timeString.slice(0, 5)); // HH:MM in Persian digits
}

function formatCurrency(amount) {
    if (!amount && amount !== 0) return '-';
    return toPersianDigits(new Intl.NumberFormat('fa-IR').format(amount));
}

// Shared height so the toolbar controls line up.
const FIELD_HEIGHT = 40;

export default function StudentSchedule() {
    const { user } = useAuth();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [orderBy, setOrderBy] = useState('created_at');
    const [orderDirection, setOrderDirection] = useState('desc');
    const [filters, setFilters] = useState({
        student_name: '',
        course_name: '',
        teacher_id: '',
        mobile: '',
        day: '',
        archive_state: 'false', // default to active student-courses
    });
    const [debouncedFilters, setDebouncedFilters] = useState(filters);
    const [refreshToken, setRefreshToken] = useState(0);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [archiveLoading, setArchiveLoading] = useState(false);
    const [formDialogOpen, setFormDialogOpen] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [renewRecord, setRenewRecord] = useState(null);
    const [formKey, setFormKey] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const location = useLocation();

    // Renewal handoff from the attendance page: the old enrollment was archived
    // there; open the registration form prefilled so the new enrollment freezes
    // the current course fee.
    useEffect(() => {
        const renewEnrollment = location.state?.renewEnrollment;
        if (renewEnrollment) {
            window.history.replaceState({}, '');
            setEditRecord(null);
            setRenewRecord(renewEnrollment);
            setFormKey(prev => prev + 1);
            setFormDialogOpen(true);
        }
    }, [location.state]);

    // Archive API hook

    // Debounce filters
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedFilters(filters);
            setPage(0); // Reset to first page when filters change
        }, 300);
        return () => clearTimeout(timer);
    }, [filters]);

    // Memoize params to prevent infinite loops.
    // archive_state is sent server-side so the fetched set is scoped to the chosen
    // status (active by default). This keeps the result under the pagination cap and
    // lets the server's `count` be the authoritative active student-course total.
    const apiParams = useMemo(() => ({
        page: 1,
        page_size: 1000, // Fetch all for client-side filtering and pagination
        archive_state: debouncedFilters.archive_state,
        _rt: refreshToken
    }), [debouncedFilters.archive_state, refreshToken]);

    // Fetch all student registrations
    const { data, loading, error, refetch } = useFetch(
        "/operations/api/v1/student-registrations/",
        { 
            params: apiParams, 
            skip: false 
        }
    );

    // Clear selection when page changes
    useEffect(() => {
        setSelectedRowId(null);
    }, [page, rowsPerPage, orderBy, orderDirection]);

    // Handle sort
    const handleSort = (fieldName) => {
        if (orderBy === fieldName) {
            // Toggle direction if same field
            setOrderDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            // New field, default to descending for dates, ascending for others
            setOrderBy(fieldName);
            setOrderDirection(fieldName.includes('date') || fieldName === 'created_at' ? 'desc' : 'asc');
        }
        setPage(0); // Reset to first page when sorting changes
    };

    // Filter and sort registrations
    const filteredRegistrations = useMemo(() => {
        const registrations = data?.results || [];
        
        // First, filter
        let filtered = registrations.filter(reg => {
            // Student name filter
            if (debouncedFilters.student_name.trim()) {
                const studentName = `${reg.student?.first_name || ''} ${reg.student?.last_name || ''}`.toLowerCase();
                if (!studentName.includes(debouncedFilters.student_name.toLowerCase())) {
                    return false;
                }
            }

            // Course name filter
            if (debouncedFilters.course_name.trim()) {
                const courseName = reg.course?.course_name?.toLowerCase() || '';
                if (!courseName.includes(debouncedFilters.course_name.toLowerCase())) {
                    return false;
                }
            }

            // Teacher filter
            if (debouncedFilters.teacher_id) {
                const teacherId = reg.course?.teacher?.id || reg.course?.teacher;
                if (teacherId !== debouncedFilters.teacher_id) {
                    return false;
                }
            }

            // Mobile filter
            if (debouncedFilters.mobile.trim()) {
                const mobile = reg.student?.mobile || '';
                if (!mobile.includes(debouncedFilters.mobile)) {
                    return false;
                }
            }

            // Day filter
            if (debouncedFilters.day !== '') {
                const day = reg.day;
                if (day !== Number(debouncedFilters.day)) {
                    return false;
                }
            }

            // Archive state filter
            if (debouncedFilters.archive_state !== '') {
                const isArchived = debouncedFilters.archive_state === 'true';
                if (reg.archive_state !== isArchived) {
                    return false;
                }
            }

            return true;
        });

        // Then, sort
        filtered.sort((a, b) => {
            let aValue, bValue;

            switch (orderBy) {
                case 'student':
                    aValue = `${a.student?.first_name || ''} ${a.student?.last_name || ''}`.toLowerCase();
                    bValue = `${b.student?.first_name || ''} ${b.student?.last_name || ''}`.toLowerCase();
                    break;
                case 'course':
                    aValue = a.course?.course_name?.toLowerCase() || '';
                    bValue = b.course?.course_name?.toLowerCase() || '';
                    break;
                case 'teacher':
                    aValue = `${a.course?.teacher?.first_name || ''} ${a.course?.teacher?.last_name || ''}`.toLowerCase();
                    bValue = `${b.course?.teacher?.first_name || ''} ${b.course?.teacher?.last_name || ''}`.toLowerCase();
                    break;
                case 'mobile':
                    aValue = a.student?.mobile || '';
                    bValue = b.student?.mobile || '';
                    break;
                case 'day':
                    aValue = a.day ?? -1;
                    bValue = b.day ?? -1;
                    break;
                case 'time':
                    aValue = a.start_time || '';
                    bValue = b.start_time || '';
                    break;
                case 'fee':
                    aValue = a.final_fee || 0;
                    bValue = b.final_fee || 0;
                    break;
                case 'created_at':
                    aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
                    bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) return orderDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return orderDirection === 'asc' ? 1 : -1;
            return 0;
        });

        return filtered;
    }, [data?.results, debouncedFilters, orderBy, orderDirection]);

    // Paginate filtered results
    const paginatedRegistrations = useMemo(() => {
        const start = page * rowsPerPage;
        const end = start + rowsPerPage;
        return filteredRegistrations.slice(start, end);
    }, [filteredRegistrations, page, rowsPerPage]);

    // Rows available for client-side pagination (after client-side narrowing filters).
    const totalCount = filteredRegistrations.length;

    // Headline count shown in the chip. When the user only scopes by status, use the
    // server's authoritative `count` (immune to the page_size cap) so it reliably means
    // "active student-courses". Once name/teacher/day/mobile narrowing is applied, fall
    // back to the client-filtered length.
    const hasClientNarrowing = Boolean(
        debouncedFilters.student_name.trim() ||
        debouncedFilters.course_name.trim() ||
        debouncedFilters.teacher_id ||
        debouncedFilters.mobile.trim() ||
        debouncedFilters.day !== ''
    );
    const displayCount = hasClientNarrowing ? totalCount : (data?.count ?? totalCount);

    // Pagination handlers
    const handleChangePage = (newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Archive handler
    const handleArchive = async (registrationId, currentArchiveState) => {
        setArchiveLoading(true);
        try {
            const response = await api.patch(`/operations/api/v1/student-registrations/${registrationId}/archive/`);
            setSnackbar({
                open: true,
                message: response.data.message || (response.data.archive_state ? 'بایگانی شد.' : 'از بایگانی خارج شد.'),
                severity: 'success'
            });
            setRefreshToken(prev => prev + 1);
        } catch (error) {
            console.error('Error archiving registration:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.detail || (currentArchiveState ? 'خروج از بایگانی انجام نشد.' : 'بایگانی انجام نشد.'),
                severity: 'error'
            });
        } finally {
            setArchiveLoading(false);
        }
    };

    // Add handler - open dialog for new registration
    const handleAdd = () => {
        setEditRecord(null);
        setRenewRecord(null);
        setFormKey(prev => prev + 1);
        setFormDialogOpen(true);
    };

    // Edit handler - open dialog with registration data
    const handleEdit = (registration) => {
        setEditRecord(registration);
        setRenewRecord(null);
        setFormKey(prev => prev + 1);
        setFormDialogOpen(true);
    };

    // Handle form submission success
    const handleFormSuccess = (action, updatedData) => {
        const wasEdit = !!editRecord;
        setFormDialogOpen(false);
        setEditRecord(null);
        setRenewRecord(null);
        setRefreshToken(prev => prev + 1);
        // Trigger refetch immediately
        setTimeout(() => {
            refetch();
        }, 100);
        setSnackbar({ 
            open: true, 
            message: wasEdit ? 'ثبت‌نام با موفقیت ویرایش شد.' : 'ثبت‌نام با موفقیت ثبت شد.', 
            severity: 'success' 
        });
    };

    // Handle snackbar close
    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    // Handle filter changes
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPage(0);
    };

    // Clear all filters
    const handleClearFilters = () => {
        setFilters({
            student_name: '',
            course_name: '',
            teacher_id: '',
            mobile: '',
            day: '',
            archive_state: 'false', // default to active student-courses
        });
        setPage(0);
    };

    // Check if any filters are active
    const hasActiveFilters = useMemo(() => {
        return Object.values(filters).some(v => v !== '' && v !== null);
    }, [filters]);


    const columns = [
        { 
            id: 'created_at',
            label: 'تاریخ ثبت‌نام',
            priority: 'secondary',
            renderHeader: (
                <TableSortLabel
                    active={orderBy === 'created_at'}
                    direction={orderBy === 'created_at' ? orderDirection : 'desc'}
                    onClick={() => handleSort('created_at')}
                    sx={{ 
                        '& .MuiTableSortLabel-icon': {
                            color: 'inherit !important'
                        }
                    }}
                >
                    تاریخ ثبت‌نام
                </TableSortLabel>
            ),
            render: (registration) => {
                const dateStr = registration.created_at || registration.created_date;
                const jalaliDate = formatJalaliDate(dateStr);
                return (
                    <Typography variant="body2">
                        {jalaliDate ? toPersianDigits(jalaliDate) : '-'}
                    </Typography>
                );
            } 
        },
        { 
            id: 'student', 
            label: 'هنرجو',
            align: 'start',
            renderHeader: (
                <TableSortLabel
                    active={orderBy === 'student'}
                    direction={orderBy === 'student' ? orderDirection : 'asc'}
                    onClick={() => handleSort('student')}
                    sx={{ 
                        '& .MuiTableSortLabel-icon': {
                            color: 'inherit !important'
                        }
                    }}
                >
                    هنرجو
                </TableSortLabel>
            ),
            render: (registration) => (
                <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                    {registration.student?.first_name || ''} {registration.student?.last_name || ''}
                </Typography>
            ) 
        },
        { 
            id: 'mobile',
            label: 'موبایل',
            priority: 'secondary',
            renderHeader: (
                <TableSortLabel
                    active={orderBy === 'mobile'}
                    direction={orderBy === 'mobile' ? orderDirection : 'asc'}
                    onClick={() => handleSort('mobile')}
                    sx={{ 
                        '& .MuiTableSortLabel-icon': {
                            color: 'inherit !important'
                        }
                    }}
                >
                    موبایل
                </TableSortLabel>
            ),
            render: (registration) => (
                <Typography variant="body2">
                    {registration.student?.mobile ? toPersianDigits(registration.student.mobile) : '-'}
                </Typography>
            ) 
        },
        { 
            id: 'course', 
            label: 'دوره',
            align: 'start',
            renderHeader: (
                <TableSortLabel
                    active={orderBy === 'course'}
                    direction={orderBy === 'course' ? orderDirection : 'asc'}
                    onClick={() => handleSort('course')}
                    sx={{ 
                        '& .MuiTableSortLabel-icon': {
                            color: 'inherit !important'
                        }
                    }}
                >
                    دوره
                </TableSortLabel>
            ),
            render: (registration) => (
                <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                    {registration.course?.course_name || '-'}
                </Typography>
            ) 
        },
        { 
            id: 'teacher',
            label: 'آموزگار',
            align: 'start',
            priority: 'secondary',
            renderHeader: (
                <TableSortLabel
                    active={orderBy === 'teacher'}
                    direction={orderBy === 'teacher' ? orderDirection : 'asc'}
                    onClick={() => handleSort('teacher')}
                    sx={{ 
                        '& .MuiTableSortLabel-icon': {
                            color: 'inherit !important'
                        }
                    }}
                >
                    آموزگار
                </TableSortLabel>
            ),
            render: (registration) => (
                <Typography variant="body2">
                    {registration.course?.teacher 
                        ? `${registration.course.teacher.first_name || ''} ${registration.course.teacher.last_name || ''}`.trim()
                        : '-'}
                </Typography>
            ) 
        },
        { 
            id: 'duration',
            label: 'مدت\n(دقیقه)',
            priority: 'secondary',
            render: (registration) => (
                <Typography variant="body2">
                    {registration.course?.duration ? `${toPersianDigits(registration.course.duration)} دقیقه` : '-'}
                </Typography>
            ) 
        },
        { 
            id: 'course_type',
            label: 'نوع\nدوره',
            priority: 'secondary',
            render: (registration) => (
                <Chip 
                    label={registration.course?.course_type === 'individual' ? 'خصوصی' : 'گروهی'}
                    size="small"
                    color={registration.course?.course_type === 'group' ? 'primary' : 'secondary'}
                    variant="outlined"
                />
            ) 
        },
        { 
            id: 'day', 
            label: 'روز',
            renderHeader: (
                <TableSortLabel
                    active={orderBy === 'day'}
                    direction={orderBy === 'day' ? orderDirection : 'asc'}
                    onClick={() => handleSort('day')}
                    sx={{ 
                        '& .MuiTableSortLabel-icon': {
                            color: 'inherit !important'
                        }
                    }}
                >
                    روز
                </TableSortLabel>
            ),
            render: (registration) => (
                <Typography variant="body2">
                    {DAY_CHOICES.find(d => d.value === registration.day)?.label || '-'}
                </Typography>
            ) 
        },
        { 
            id: 'time', 
            label: 'ساعت',
            renderHeader: (
                <TableSortLabel
                    active={orderBy === 'time'}
                    direction={orderBy === 'time' ? orderDirection : 'asc'}
                    onClick={() => handleSort('time')}
                    sx={{ 
                        '& .MuiTableSortLabel-icon': {
                            color: 'inherit !important'
                        }
                    }}
                >
                    ساعت
                </TableSortLabel>
            ),
            render: (registration) => (
                <Typography variant="body2">
                    {formatTime(registration.start_time)} - {formatTime(registration.end_time)}
                </Typography>
            ) 
        },
        { 
            id: 'fee',
            label: 'شهریه',
            priority: 'secondary',
            renderHeader: (
                <TableSortLabel
                    active={orderBy === 'fee'}
                    direction={orderBy === 'fee' ? orderDirection : 'asc'}
                    onClick={() => handleSort('fee')}
                    sx={{ 
                        '& .MuiTableSortLabel-icon': {
                            color: 'inherit !important'
                        }
                    }}
                >
                    شهریه
                </TableSortLabel>
            ),
            render: (registration) => (
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                        {formatCurrency(registration.final_fee || 0)} ریال
                    </Typography>
                    {registration.discount_amount > 0 && (
                        <Typography variant="caption" color="success.main" display="block">
                            تخفیف: {formatCurrency(registration.discount_amount)} ریال
                        </Typography>
                    )}
                </Box>
            ) 
        },
        { 
            id: 'status', 
            label: 'وضعیت', 
            compact: true,
            render: (registration) => (
                <Chip
                    label={registration.archive_state ? 'بایگانی' : 'فعال'}
                    color={registration.archive_state ? 'default' : 'success'}
                    size="small"
                    variant="outlined"
                />
            ) 
        },
        { 
            id: 'actions', 
            label: 'عملیات', 
            compact: true,
            render: (registration) => (
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(registration);
                        }}
                        size="small"
                        color="primary"
                        aria-label="ویرایش ثبت‌نام"
                        title="ویرایش ثبت‌نام"
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation();
                            handleArchive(registration.id, registration.archive_state);
                        }}
                        size="small"
                        color={registration.archive_state ? 'success' : 'default'}
                        disabled={archiveLoading}
                        aria-label={registration.archive_state ? 'خروج از بایگانی' : 'بایگانی'}
                        title={registration.archive_state ? 'خروج از بایگانی' : 'بایگانی'}
                    >
                        {registration.archive_state ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
                    </IconButton>
                </Box>
            ) 
        }
    ];

    return (
        <RTL>
            <Box sx={{ p: 2 }}>
                {formDialogOpen ? (
                    <Box>
                        <Button
                            variant="text"
                            startIcon={<BackIcon />}
                            onClick={() => { setFormDialogOpen(false); setEditRecord(null); setRenewRecord(null); }}
                            sx={{ mb: 2 }}
                        >
                            بازگشت به فهرست
                        </Button>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
                            {editRecord ? 'ویرایش ثبت‌نام' : (renewRecord ? 'ثبت‌نام با شهریه جدید' : 'افزودن ثبت‌نام جدید')}
                        </Typography>
                        {renewRecord && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                ثبت‌نام قبلی این هنرجو بایگانی شد. با ثبت‌نام جدید، شهریه روز دوره ملاک محاسبه خواهد بود.
                            </Alert>
                        )}
                        <Paper sx={{ p: 3 }}>
                            <StudentRegistrationForm
                                formKey={formKey}
                                userRole={user?.staff_role?.name || (user?.roles?.[0] ?? "")}
                                mode={editRecord ? 'edit' : 'create'}
                                editRecord={editRecord}
                                renewRecord={renewRecord}
                                onAfterSubmit={handleFormSuccess}
                                onStudentChange={() => {}}
                            />
                        </Paper>
                    </Box>
                ) : (
                <Paper sx={{ p: 3, mb: 3 }}>
                    {/* Header: title + count, primary action */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            <Box sx={{ display: 'flex', color: 'primary.main' }}><EventNoteOutlinedIcon fontSize="small" /></Box>
                            <Typography variant="h6" sx={{ m: 0, fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                                برنامه هنرجویان
                            </Typography>
                            {displayCount > 0 && (
                                <Chip size="small" variant="outlined" color="primary" label={toPersianDigits(displayCount)} />
                            )}
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAdd}
                            sx={{ whiteSpace: 'nowrap', height: FIELD_HEIGHT, m: 0 }}
                        >
                            افزودن ثبت‌نام
                        </Button>
                    </Box>

                    {/* Toolbar: filters (flat, unified height) */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3, '& .MuiTextField-root': { m: 0 }, '& .MuiFormControl-root': { m: 0 } }}>
                        <TextField
                            size="small"
                            label="نام هنرجو"
                            placeholder="جستجوی نام هنرجو..."
                            value={filters.student_name}
                            onChange={(e) => handleFilterChange('student_name', e.target.value)}
                            sx={{ flex: '1 1 170px', minWidth: 150 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: filters.student_name && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => handleFilterChange('student_name', '')} edge="end">
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            size="small"
                            label="نام دوره"
                            placeholder="جستجوی نام دوره..."
                            value={filters.course_name}
                            onChange={(e) => handleFilterChange('course_name', e.target.value)}
                            sx={{ flex: '1 1 170px', minWidth: 150 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: filters.course_name && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => handleFilterChange('course_name', '')} edge="end">
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Box sx={{ flex: '1 1 170px', minWidth: 150 }}>
                            <SearchableAutocomplete
                                apiUrl="/accounts/api/v1/profile/teachers/"
                                label="آموزگار"
                                placeholder="انتخاب آموزگار..."
                                value={filters.teacher_id}
                                onChange={(value) => handleFilterChange('teacher_id', value || '')}
                                getOptionLabel={(option) => {
                                    if (!option) return '';
                                    const user = option?.user;
                                    if (!user) return '';
                                    const first = user.first_name?.trim() || '';
                                    const last = user.last_name?.trim() || '';
                                    const full = `${first} ${last}`.trim();
                                    return full || `آموزگار #${option.id}`;
                                }}
                                getOptionValue={(option) => option?.user?.id}
                                size="small"
                            />
                        </Box>

                        <TextField
                            size="small"
                            label="موبایل"
                            placeholder="جستجوی موبایل..."
                            value={filters.mobile}
                            onChange={(e) => handleFilterChange('mobile', e.target.value)}
                            sx={{ flex: '1 1 150px', minWidth: 130 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: filters.mobile && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => handleFilterChange('mobile', '')} edge="end">
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <FormControl size="small" sx={{ minWidth: 130 }}>
                            <InputLabel>روز</InputLabel>
                            <Select
                                value={filters.day}
                                onChange={(e) => handleFilterChange('day', e.target.value)}
                                label="روز"
                            >
                                <MenuItem value="">همه روزها</MenuItem>
                                {DAY_CHOICES.map(day => (
                                    <MenuItem key={day.value} value={day.value}>
                                        {day.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 130 }}>
                            <InputLabel>وضعیت</InputLabel>
                            <Select
                                value={filters.archive_state}
                                onChange={(e) => handleFilterChange('archive_state', e.target.value)}
                                label="وضعیت"
                            >
                                <MenuItem value="">همه وضعیت‌ها</MenuItem>
                                <MenuItem value="false">فعال</MenuItem>
                                <MenuItem value="true">بایگانی</MenuItem>
                            </Select>
                        </FormControl>

                        {hasActiveFilters && (
                            <Button
                                startIcon={<ClearIcon />}
                                onClick={handleClearFilters}
                                size="small"
                                variant="outlined"
                                sx={{ whiteSpace: 'nowrap', height: FIELD_HEIGHT, m: 0 }}
                            >
                                پاک کردن فیلترها
                            </Button>
                        )}
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            خطا در بارگذاری داده‌ها: {error.message || error}
                        </Alert>
                    )}

                    <StandardTable
                        columns={columns}
                        rows={paginatedRegistrations}
                        loading={loading}
                        error={error}
                        emptyText={hasActiveFilters ? "هیچ ثبت‌نامی با این فیلترها یافت نشد." : "هیچ ثبت‌نامی یافت نشد."}
                        getRowKey={(r) => r.id}
                        ariaLabel="student-schedule"
                        minWidth={620}
                        onRowClick={handleEdit}
                        getRowProps={(registration) => ({
                            selected: selectedRowId === registration.id,
                            sx: selectedRowId === registration.id ? {
                                backgroundColor: 'action.selected',
                            } : undefined
                        })}
                    />

                    {totalCount > 0 && (
                        <TablePagination
                            totalCount={totalCount}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            pageSizeOptions={[10, 15, 25, 50, 100]}
                            responsive={true}
                        />
                    )}
                </Paper>
                )}

                {/* Snackbar */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </RTL>
    );
}

